import { Box } from "@mui/material";
import { FC, useCallback, useRef, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { ICustomField, IDistance, isOverlapping, Log } from "@ryde/shared";

import { ColorIconButton } from "../../controls";
import { NumberTemplate } from "./number.template";

interface ISpecificDistanceProps {
  from: number;
  to: number;
  price: number;
  onEdit?: () => void;
  onClear?: () => void;
}

const SpecificDistance: FC<ISpecificDistanceProps> = ({
  from,
  to,
  price,
  onEdit,
  onClear,
}) => {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr 1fr",
        alignItems: "center",
      }}
    >
      <p>
        {from} - {to}
      </p>
      <p>{price}</p>
      <Box>
        <ColorIconButton iconType="EDIT" onClick={onEdit} />
        <ColorIconButton iconType="DELETE" onClick={onClear} />
      </Box>
    </Box>
  );
};

export const DistanceAdditions: FC<ICustomField> = ({ name }) => {
  const { t } = useTranslation();
  const outerMethods = useFormContext();
  outerMethods.register(name);
  const defaultPriceFieldName = `${name}.defaultPrice`;
  const pricingFieldName = `${name}.prices`;
  outerMethods.register(pricingFieldName);

  const methods = useForm();

  const { getValues, resetField, setValue } = methods;

  const [specificDistances, setSpecificDistances] = useState<IDistance[]>(
    outerMethods?.getValues(name)?.prices || [],
  );

  const handleAddSpecificDistance = useCallback((): void => {
    const from = getValues("fromDistance");
    const to = getValues("toDistance");
    const price = getValues("extraPrice");

    const shouldAdd = from && to && price;

    setSpecificDistances(prev => {
      const newValue = [
        ...prev,
        ...(shouldAdd
          ? [
              {
                from,
                to,
                price,
              },
            ]
          : []),
      ];

      const overlap = prev.some(distance =>
        isOverlapping(distance, { from, to, price }),
      );

      if (!overlap) {
        outerMethods.setValue(pricingFieldName, newValue, {
          shouldDirty: true,
        });
        return newValue;
      } else {
        Log.warnAndToast("errors.overlappingDistancesNotAllowed");
        return prev;
      }
    });
    resetField("fromDistance");
    resetField("toDistance");
    resetField("extraPrice");
  }, [getValues, outerMethods, pricingFieldName, resetField]);

  const handleClearSpecificDistanceForm = useCallback((): void => {
    setSpecificDistances(prev =>
      distancesSnapshot.current.length > 0 ? distancesSnapshot.current : prev,
    );
    resetField("fromDistance");
    resetField("toDistance");
    resetField("extraPrice");
  }, [resetField]);

  const handleEditSpecificDistance = useCallback(
    (from: number, to: number, price: number): void => {
      setSpecificDistances(prev => {
        distancesSnapshot.current = prev;
        return (prev || []).filter(
          distance => distance.from !== from && distance.to !== to,
        );
      });
      setValue("fromDistance", from);
      setValue("toDistance", to);
      setValue("extraPrice", price);
    },
    [setValue],
  );

  const handleClearSpecificDistance = (from: number, to: number): void => {
    setSpecificDistances(prev =>
      (prev || []).filter(
        distance => distance.from !== from && distance.to !== to,
      ),
    );
  };

  const distancesSnapshot = useRef<IDistance[]>([]);

  return (
    <Box
      sx={{
        width: 600,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
      data-e2e-template="distance-additions-template"
    >
      <NumberTemplate
        formLabel={t("priceRate.additionForSpecificExtraAreas")}
        name={defaultPriceFieldName}
        id={1}
      />

      {specificDistances?.length &&
        specificDistances.map(({ from, to, price }: IDistance, i) => (
          <SpecificDistance
            key={i}
            from={from}
            to={to}
            price={price}
            onEdit={() => handleEditSpecificDistance(from, to, price)}
            onClear={() => handleClearSpecificDistance(from, to)}
          />
        ))}
      <FormProvider {...methods}>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-end",
            gap: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 3,
              width: "100%",
            }}
          >
            <NumberTemplate
              formLabel={t("priceRate.fromKM")}
              name="fromDistance"
            />{" "}
            <NumberTemplate formLabel={t("priceRate.toKM")} name="toDistance" />
            <NumberTemplate
              formLabel={t("priceRate.additionForSpecificExtraAreas")}
              name="extraPrice"
            />
          </Box>
          <Box sx={{ display: "flex" }}>
            <ColorIconButton
              iconType="CHECK"
              onClick={handleAddSpecificDistance}
            />
            <ColorIconButton
              iconType="CANCEL"
              onClick={handleClearSpecificDistanceForm}
            />
          </Box>
        </Box>
      </FormProvider>
    </Box>
  );
};
